import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { DietViewStep } from "@/components/diet/DietViewStep";
import { diets } from "@/i18n/locales/diets";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigation } from "@/components/Navigation";

const ViewDiet = () => {
    const { t, i18n } = useTranslation();
    const { currentDietId, authFetch } = useAuth();
    const navigate = useNavigate();

    const {
        data: activeDiet,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey: ["diet", currentDietId], // Query key
        queryFn: async () => {
            const response = await authFetch("/api/diet/" + currentDietId);
            if (!response.ok) {
                throw new Error("Failed to fetch the active diet");
            }
            const diet = (await response.json()).diet;
            return diet;
        },

        retry: 1, // Ritenta una sola volta in caso di errore
    });

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />

                <div className="container mx-auto p-6">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-32 bg-gray-200 rounded"></div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!activeDiet) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navigation />
                <div className="container mx-auto p-6">
                    <h1 className="text-2xl font-bold mb-4">{t("dashboard.diet.noDiet")}</h1>
                    <Button onClick={() => navigate("/build-diet")}>{t("dashboard.diet.create")}</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="container mx-auto p-6 space-y-6 max-w-6xl">
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("nav.dashboard")}
                </Button>

                <div className="grid gap-6">
                    <DietViewStep />

                    <Card className="p-6">
                        <h2 className="text-xl font-semibold mb-4">{t("dashboard.diet.details")}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">{t("dashboard.diet.initialWeight")}</p>
                                <p className="text-lg font-medium">
                                    {activeDiet.weight} {t("dashboard.diet.kg")}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">{t("dashboard.diet.startDate")}</p>
                                <p className="text-lg font-medium">{new Date(activeDiet.createdAt).toLocaleDateString()}</p>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ViewDiet;
